import React from "react";
import { Typography, Box } from "@mui/material";
import SimplePaper from "../../../components/SimplePaper";
import { MathComponent } from "mathjax-react";

const D30fromD1 = () => {
  const title = "Calculation of D30 (Trimsamsa) from D1 (Rasi)";

  const body = (
    <>
      <Typography variant="body1" paragraph>
        The D30 chart, or Trimsamsa chart, is used in Vedic astrology to analyze
        challenges, misfortunes, health issues, and obstacles. Below is the
        step-by-step process to calculate the D30 chart from the D1 (Rasi)
        chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Procedure to Calculate D30 (Trimsamsa) from D1 (Rasi)
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Identify the Planet's Degree in the D1 Chart
      </Typography>

      <Typography variant="body1" paragraph>
        First, identify the degree of the planet in the D1 chart. For example,
        if Mars is located at 12° Aries, this degree will be used for the
        calculation.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Divide the Sign into Thirty Parts
      </Typography>

      <Typography variant="body1" paragraph>
        Each zodiac sign is divided into thirty equal parts, each spanning 1°.
        To find the Trimsamsa position of a planet, we divide its degree in the
        sign by 1°.
      </Typography>

      <Box sx={{ textAlign: "center", margin: "1rem 0" }}>
        <MathComponent
          tex={String.raw`Trimsamsa \ Position = \frac{Degree \ in \ Sign}{1}`}
        />
      </Box>

      <Typography variant="body1" paragraph>
        Depending on the result, we can determine the section in which the
        planet lies:
      </Typography>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`0 \leq \frac{Degree \ in \ Sign}{1} < 1`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>first 1°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`1 \leq \frac{Degree \ in \ Sign}{1} < 2`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>second 1°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`2 \leq \frac{Degree \ in \ Sign}{1} < 3`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>third 1°</strong>.
        </Typography>
      </Box>

      {/* Add similar blocks for all 30 parts */}

      <Typography variant="h5" gutterBottom>
        3. Assign the Trimsamsa Based on the Planet's Degree
      </Typography>

      <Typography variant="body1" paragraph>
        <strong>First 1°:</strong> The planet is assigned to the{" "}
        <strong>same sign</strong> as the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Second 1°:</strong> The planet is assigned to the{" "}
        <strong>2nd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Third 1°:</strong> The planet is assigned to the{" "}
        <strong>3rd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fourth 1°:</strong> The planet is assigned to the{" "}
        <strong>4th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fifth 1°:</strong> The planet is assigned to the{" "}
        <strong>5th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Sixth 1°:</strong> The planet is assigned to the{" "}
        <strong>6th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Seventh 1°:</strong> The planet is assigned to the{" "}
        <strong>7th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Eighth 1°:</strong> The planet is assigned to the{" "}
        <strong>8th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Ninth 1°:</strong> The planet is assigned to the{" "}
        <strong>9th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Tenth 1°:</strong> The planet is assigned to the{" "}
        <strong>10th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Eleventh 1°:</strong> The planet is assigned to the{" "}
        <strong>11th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twelfth 1°:</strong> The planet is assigned to the{" "}
        <strong>12th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Thirteenth 1°:</strong> The planet is assigned to the{" "}
        <strong>13th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fourteenth 1°:</strong> The planet is assigned to the{" "}
        <strong>14th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fifteenth 1°:</strong> The planet is assigned to the{" "}
        <strong>15th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Sixteenth 1°:</strong> The planet is assigned to the{" "}
        <strong>16th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Seventeenth 1°:</strong> The planet is assigned to the{" "}
        <strong>17th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Eighteenth 1°:</strong> The planet is assigned to the{" "}
        <strong>18th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Nineteenth 1°:</strong> The planet is assigned to the{" "}
        <strong>19th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twentieth 1°:</strong> The planet is assigned to the{" "}
        <strong>20th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twenty-first 1°:</strong> The planet is assigned to the{" "}
        <strong>21st sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twenty-second 1°:</strong> The planet is assigned to the{" "}
        <strong>22nd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twenty-third 1°:</strong> The planet is assigned to the{" "}
        <strong>23rd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twenty-fourth 1°:</strong> The planet is assigned to the{" "}
        <strong>24th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twenty-fifth 1°:</strong> The planet is assigned to the{" "}
        <strong>25th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twenty-sixth 1°:</strong> The planet is assigned to the{" "}
        <strong>26th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twenty-seventh 1°:</strong> The planet is assigned to the{" "}
        <strong>27th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twenty-eighth 1°:</strong> The planet is assigned to the{" "}
        <strong>28th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Twenty-ninth 1°:</strong> The planet is assigned to the{" "}
        <strong>29th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Thirtieth 1°:</strong> The planet is assigned to the{" "}
        <strong>30th sign</strong> from the D1 sign.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Example: Calculating Mars at 12° Aries
      </Typography>

      <Typography variant="body1" paragraph>
        Aries is the D1 sign. Mars at 12° falls in the thirteenth 1° (between
        12° and 13°). Therefore, it belongs to the <strong>13th sign</strong>{" "}
        from Aries, which is <strong>Leo</strong>
      </Typography>

      <Typography variant="h4" gutterBottom>
        Python Program to Calculate D30 (Trimsamsa) from D1
      </Typography>

      <Typography variant="body1" paragraph>
        Below is a Python program that performs the calculation of the D30
        (Trimsamsa) position from the D1 (Rasi) chart, using the planet's degree
        and sign in the D1 chart.
      </Typography>

      <Box
        component="pre"
        sx={{
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: "5px",
        }}
      >
        <code>
          {`def calculate_trimsamsa(degree, sign):
    # List of zodiac signs in order
    zodiac_signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces']

    # Get the index of the current sign in the zodiac cycle
    sign_index = zodiac_signs.index(sign)

    # Determine which Trimsamsa the planet is in based on its degree
    if degree <= 1:
        # First 1° belongs to the same sign
        trimsamsa_sign = zodiac_signs[sign_index]
    elif 1 < degree <= 2:
        # Second 1° belongs to the 2nd sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    elif 2 < degree <= 3:
        # Third 1° belongs to the 3rd sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    elif 3 < degree <= 4:
        # Fourth 1° belongs to the 4th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    elif 4 < degree <= 5:
        # Fifth 1° belongs to the 5th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    elif 5 < degree <= 6:
        # Sixth 1° belongs to the 6th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 5) % 12]
    elif 6 < degree <= 7:
        # Seventh 1° belongs to the 7th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 6) % 12]
    elif 7 < degree <= 8:
        # Eighth 1° belongs to the 8th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 7) % 12]
    elif 8 < degree <= 9:
        # Ninth 1° belongs to the 9th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 8) % 12]
    elif 9 < degree <= 10:
        # Tenth 1° belongs to the 10th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 9) % 12]
    elif 10 < degree <= 11:
        # Eleventh 1° belongs to the 11th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 10) % 12]
    elif 11 < degree <= 12:
        # Twelfth 1° belongs to the 12th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 11) % 12]
    elif 12 < degree <= 13:
        # Thirteenth 1° belongs to the 13th sign from the current sign
        trimsamsa_sign = zodiac_signs[sign_index]
    elif 13 < degree <= 14:
        # Fourteenth 1° belongs to the 14th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    elif 14 < degree <= 15:
        # Fifteenth 1° belongs to the 15th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    elif 15 < degree <= 16:
        # Sixteenth 1° belongs to the 16th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    elif 16 < degree <= 17:
        # Seventeenth 1° belongs to the 17th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    elif 17 < degree <= 18:
        # Eighteenth 1° belongs to the 18th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 5) % 12]
    elif 18 < degree <= 19:
        # Nineteenth 1° belongs to the 19th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 6) % 12]
    elif 19 < degree <= 20:
        # Twentieth 1° belongs to the 20th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 7) % 12]
    elif 20 < degree <= 21:
        # Twenty-first 1° belongs to the 21st sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 8) % 12]
    elif 21 < degree <= 22:
        # Twenty-second 1° belongs to the 22nd sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 9) % 12]
    elif 22 < degree <= 23:
        # Twenty-third 1° belongs to the 23rd sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 10) % 12]
    elif 23 < degree <= 24:
        # Twenty-fourth 1° belongs to the 24th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 11) % 12]
    elif 24 < degree <= 25:
        # Twenty-fifth 1° belongs to the 25th sign from the current sign
        trimsamsa_sign = zodiac_signs[sign_index]
    elif 25 < degree <= 26:
        # Twenty-sixth 1° belongs to the 26th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    elif 26 < degree <= 27:
        # Twenty-seventh 1° belongs to the 27th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 2) % 12]
    elif 27 < degree <= 28:
        # Twenty-eighth 1° belongs to the 28th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 3) % 12]
    elif 28 < degree <= 29:
        # Twenty-ninth 1° belongs to the 29th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    else:
        # Thirtieth 1° belongs to the 30th sign from the current sign
        trimsamsa_sign = zodiac_signs[(sign_index + 5) % 12]

    return trimsamsa_sign

# Example usage
planet = "Mars"
degree = 12
sign = "Aries"

trimsamsa_sign = calculate_trimsamsa(degree, sign)
print(f"{planet} at {degree}° {sign} in the D1 chart is placed in {trimsamsa_sign} in the D30 chart.")`}
        </code>
      </Box>

      <Typography variant="body1" paragraph>
        The above Python program calculates the Trimsamsa for a planet based on
        its degree and sign in the D1 chart. The program divides the sign into
        thirty equal parts and assigns the Trimsamsa based on the planet's
        degree.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Explanation of the Formula Used in the Python Program
      </Typography>

      <Typography variant="body1" paragraph>
        The program divides the sign into thirty parts: the first 1°, the second
        1°, and so on. It assigns the Trimsamsa based on the planet's degree
        within the sign:
        <br />
        - First 1° belongs to the same sign.
        <br />
        - Second 1° belongs to the 2nd sign from the D1 sign.
        <br />
        - Third 1° belongs to the 3rd sign from the D1 sign.
        <br />
        - Fourth 1° belongs to the 4th sign from the D1 sign.
        <br />
        - Fifth 1° belongs to the 5th sign from the D1 sign.
        <br />
        - Sixth 1° belongs to the 6th sign from the D1 sign.
        <br />
        - Seventh 1° belongs to the 7th sign from the D1 sign.
        <br />
        - Eighth 1° belongs to the 8th sign from the D1 sign.
        <br />
        - Ninth 1° belongs to the 9th sign from the D1 sign.
        <br />
        - Tenth 1° belongs to the 10th sign from the D1 sign.
        <br />
        - Eleventh 1° belongs to the 11th sign from the D1 sign.
        <br />
        - Twelfth 1° belongs to the 12th sign from the D1 sign.
        <br />
        - Thirteenth 1° belongs to the 13th sign from the D1 sign.
        <br />
        - Fourteenth 1° belongs to the 14th sign from the D1 sign.
        <br />
        - Fifteenth 1° belongs to the 15th sign from the D1 sign.
        <br />
        - Sixteenth 1° belongs to the 16th sign from the D1 sign.
        <br />
        - Seventeenth 1° belongs to the 17th sign from the D1 sign.
        <br />
        - Eighteenth 1° belongs to the 18th sign from the D1 sign.
        <br />
        - Nineteenth 1° belongs to the 19th sign from the D1 sign.
        <br />
        - Twentieth 1° belongs to the 20th sign from the D1 sign.
        <br />
        - Twenty-first 1° belongs to the 21st sign from the D1 sign.
        <br />
        - Twenty-second 1° belongs to the 22nd sign from the D1 sign.
        <br />
        - Twenty-third 1° belongs to the 23rd sign from the D1 sign.
        <br />
        - Twenty-fourth 1° belongs to the 24th sign from the D1 sign.
        <br />
        - Twenty-fifth 1° belongs to the 25th sign from the D1 sign.
        <br />
        - Twenty-sixth 1° belongs to the 26th sign from the D1 sign.
        <br />
        - Twenty-seventh 1° belongs to the 27th sign from the D1 sign.
        <br />
        - Twenty-eighth 1° belongs to the 28th sign from the D1 sign.
        <br />
        - Twenty-ninth 1° belongs to the 29th sign from the D1 sign.
        <br />- Thirtieth 1° belongs to the 30th sign from the D1 sign.
      </Typography>

      <Typography variant="body1" paragraph>
        This method programmatically reproduces the manual calculation and can
        be easily adjusted for different planets, degrees, and signs.
      </Typography>
    </>
  );

  return <SimplePaper title={title} body={body} />;
};

export default D30fromD1;
